import React, { useState, useEffect } from "react";
import { Table, Row, Col, Spin, Button } from "antd";
import { connect } from "react-redux";
import { Modal } from "antd";
import CreateUpdateForm from "./CreateUpdateForm";
import DeleteForm from "./DeleteForm";
import InformationForm from "./InformationForm";
import { createInformation, createOpportunity, deleteOpportunity, getOpportunity, updateOpportunity, updateInformation, getInformation } from "../../store/actions/opportunityActions";
import { getBase64, uploadOtherFilesToCloudflare } from "../../utils/uploadFileToCloudflare";

const initialPayload = {
  opportunityName: "",
  coins: [],
  description: "",
  longDescription: "",
  thumbnail: "",
  type: "",
  risk: "",
  yield: 0,
  contractAge: 0,
  currentTVL: 0,
  allocation: 0,
  playLink: "",
  externalLink: "",
};

const PassiveIncome = ({
  getOpportunity,
  createOpportunity,
  updateOpportunity,
  deleteOpportunity,
  createInformation,
  updateInformation,
  getInformation,
  information,
  opportunities,
  opportunity,
 }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [category, setCategory] = useState("createCoin");
  const [modalTitle, setModalTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailURL, setThumbnailURL] = useState("");
  const [opportunityPayload, setOpportunityPayload] = useState(initialPayload);
  const [infoPayload, setInfoPayload] = useState({
    text: ""
  });

  useEffect(() => {
    getOpportunity();
    getInformation();
  }, [getOpportunity, getInformation]);
  
  useEffect(() => {
    if (information) {
      setInfoPayload(information);
    }
  }, [information]);

  const columns = [
     {
      title: "Id",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Opportunity Name",
      dataIndex: "opportunityName",
      key: "opportunityName",
    },
    {
      title: "Coins",
      dataIndex: "coins",
      key: "coins",
      render: (coins) => (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {coins?.map((coin, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {coin.image && <img src={coin.image} alt={coin.name} style={{ width: '16px', height: '16px' }} />}
              <span>{coin.name}</span>
              {index < coins.length - 1 && <span>,</span>}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Yield (%)",
      dataIndex: "yield",
      key: "yield",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.yield - b.yield,
      render: (value) => `${value}%`,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.type.localeCompare(b.type),
      render: (value) => {
        const typeMap = {
          "flutuante": "Flutuante",
          "prefixado": "Pré-fixado",
          "preFixado": "Pré-fixado"
        };
        return typeMap[value] || value || "-";
      },
    },
    {
      title: "Risk",
      dataIndex: "risk",
      key: "risk",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.risk.localeCompare(b.risk),
    },
    {
      title: "Action",
      dataIndex: "",
      key: "x",
      render: (_, record) => (
        <div>
          <Row gutter={4}>
            <Col>
              <Button
                type="primary"
                onClick={() => showModal("editOpportunity", record)}
              >
                Edit
              </Button>
            </Col>

            <Col>
              <Button danger onClick={() => showModal("deleteOpportunity", record)}>
                Delete
              </Button>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

   const showModal = (type, record) => {
    if (type === "createOpportunity") {
      setThumbnailURL(""); // Clear thumbnail for new opportunity
      setCategory("createOpportunity");
      setModalTitle("Create Opportunity");
    }

    if (type === "editOpportunity" && record) {
      // Convert currentTVL from base value to millions to prevent double multiplication
      setOpportunityPayload({
        ...record,
        currentTVL: record.currentTVL / 1000000
      });
      // Set thumbnail URL from backend thumb field
      setThumbnailURL(record.thumb || "");
      setCategory("editOpportunity");
      setModalTitle("Edit Opportunity");
    }

    if (type === "deleteOpportunity" && record) {
      setOpportunityPayload(record);
      setCategory("deleteOpportunity");
      setModalTitle(`Delete ${record.opportunityName}`);
    }

    if (type === "addInformation") {
      setCategory("addInformation");
      setModalTitle(`Add Information`);
    }

    if (type === "editInformation" && record) {
      setInfoPayload(record);
      setCategory("editInformation");
      setModalTitle(`Edit Information`);
    }

    setIsModalVisible(true);
  };

  const handleOk = async () => {
    setLoading(true);

    if (category === "createOpportunity") {
      const fileURL = await uploadOtherFilesToCloudflare(
        thumbnail.file.originFileObj,
        "opportunity"
      );
      const finalPayload = {
        ...opportunityPayload,    
        thumbnail: fileURL,
      };
      await createOpportunity(finalPayload);
    }

    if (category === "editOpportunity") {
      let fileURL;
      if (thumbnail && thumbnailURL) {  
        fileURL = await uploadOtherFilesToCloudflare(
          thumbnail.file.originFileObj,
          "opportunity"
        );
      } else {
        fileURL = thumbnailURL;
      }
      const finalPayload = {
        ...opportunityPayload,
        thumbnail: fileURL,
      };
      await updateOpportunity(finalPayload);
    }

    if (category === "deleteOpportunity") {
      await deleteOpportunity(opportunityPayload.id || opportunityPayload._id);
    }

    if (category === "addInformation") {
      await createInformation(infoPayload);
    }
    
    if (category === "editInformation") {
      await updateInformation(infoPayload);
    }

    setLoading(false);
    setIsModalVisible(false);
    setOpportunityPayload(initialPayload);
    await getOpportunity();
  };
  
  const handleUpload = (upload) => {
    getBase64(upload.file.originFileObj, (imageUrl) => {
      setThumbnail(upload);
      setThumbnailURL(imageUrl);
    });
  };

  return (
    <>
      <div>
        <Row gutter={4}>
          <Col>
            <Button type="primary" onClick={() => showModal("createOpportunity")}> 
              Create Opportunity
            </Button>
          </Col>

          <Col>
            <Button 
              type="primary" 
              onClick={() => showModal(information?.id ? "editInformation" : "addInformation", information)}
            > 
              Information
            </Button>
          </Col>
        </Row>

        <div style={{ marginTop: 20 }}>
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={Array.isArray(opportunities) ? opportunities : []}
              rowKey={record => record.id || record._id}
              pagination={false}
            />
          </Spin>
        </div>

        <Modal
          key={isModalVisible ? "open" : "closed"}
          title={modalTitle}
          open={isModalVisible}
          onOk={handleOk}
          onCancel={() => {
            setIsModalVisible(false);
          }}
          footer={[
            <Button key="back" onClick={() => setIsModalVisible(false)}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              danger={category === "deleteOpportunity"}
              loading={loading}
              onClick={handleOk}
            >
              {category === "createOpportunity" 
                ? "Create"
                : category === "editOpportunity"
                ? "Update"
                : category === "deleteOpportunity"
                ? "Delete"
                : "Done"} 
            </Button>,
          ]}
        >
          {category === "createOpportunity" || category === "editOpportunity" ? (
            <CreateUpdateForm
              opportunityError={null}
              payload={opportunityPayload}
              handlePayloadChange={setOpportunityPayload}
              category={category}
              response={opportunity}
              thumbnailURL={thumbnailURL}
              handleUpload={handleUpload} 
            />
          ) : category === "deleteOpportunity" ? (
            <DeleteForm />
          ) : category === "addInformation" || category === "editInformation" ? (
            <InformationForm 
              payload={infoPayload} 
              handlePayloadChange={setInfoPayload} 
              category={category}
              response={information}
            />
          ) : (
            <div>No form category selected</div>
          )}
        </Modal>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  opportunities: state.opportunities.opportunities,
  opportunity: state.opportunities.opportunity,
  information: state.opportunities.information,
})

export default connect(mapStateToProps, {
  getOpportunity,
  createOpportunity,
  updateOpportunity,
  deleteOpportunity,
  createInformation,
  updateInformation,
  getInformation,
})(PassiveIncome);
